import React, { useState } from 'react';
import { authService } from '../../services/authService';
import { useAuthStore } from '../../stores/authStore';

interface Props {
  /** Opens the existing account panel, where the address is entered and confirmed. */
  onManageEmail: () => void;
}

function formatExpiry(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Asks a returning guest to attach an address so their account stops being disposable.
 *
 * Shares the marketing prompt's banner treatment and lobby slot, but not its dismissal semantics:
 * "Not now" snoozes rather than refuses forever, because the thing at stake is the player's
 * characters rather than a newsletter.
 *
 * Carries no marketing consent of any kind. Securing an account and joining a mailing list are
 * different asks, and bundling them would make the consent worthless.
 */
export const AccountLinkPrompt: React.FC<Props> = ({ onManageEmail }) => {
  const user = useAuthStore((s) => s.user);
  const patchUser = useAuthStore((s) => s.patchUser);
  const [busy, setBusy] = useState(false);

  if (!user?.showAccountLinkPrompt) return null;

  const expiry = formatExpiry(user.sessionExpiresAt);

  const handleSnooze = async () => {
    setBusy(true);
    // Hidden at once and regardless of the outcome — a player who said "not now" should not wait on
    // a spinner. If the write fails the prompt simply returns sooner than intended, which is the
    // harmless direction for this particular ask to fail in.
    //
    // The marketing prompt is suppressed alongside it, for this visit only. A guest with no address
    // qualifies for both, and the lobby shows whichever wins — so without this, declining one ask
    // would immediately produce another in the same spot, which is how a prompt earns being ignored.
    // Local only: the server is untouched, so it is offered again on the next /auth/me.
    patchUser({ showAccountLinkPrompt: false, showMarketingPrompt: false });
    try {
      await authService.dismissAccountLinkPrompt();
    } catch {
      // Snoozing is best-effort; the criteria are re-evaluated on the next /auth/me anyway.
    }
  };

  const buttonStyle: React.CSSProperties = {
    padding: '6px 14px',
    borderRadius: 4,
    border: 'none',
    fontFamily: 'monospace',
    fontSize: 12,
    fontWeight: 'bold',
    cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div
      className="optin-banner"
      style={{
        background: 'rgba(216, 166, 87, 0.08)',
        border: '1px solid rgba(216, 166, 87, 0.35)',
        borderRadius: 6,
        padding: '10px 14px',
        marginBottom: 18,
      }}
    >
      <div style={{ color: '#e8e8ff', fontSize: 13, marginBottom: 6 }}>
        Save your progress before you lose it
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ color: '#999', fontSize: 12, lineHeight: 1.45 }}>
            This is a guest account, so it only exists in this browser. Clear your history, switch
            device{expiry ? `, or leave it until ${expiry}` : ''}, and your characters are gone for
            good — there is no way for me to recover them. Adding an email address fixes that, and
            lets you sign in from anywhere.
          </div>
        </div>
        {/* Row vs column is decided by .optin-actions in App.css, from the banner's own width. */}
        <div className="optin-actions">
          <button
            onClick={onManageEmail}
            disabled={busy}
            style={{ ...buttonStyle, background: '#d8a657', color: '#1a1a2e' }}
          >
            Add email
          </button>
          <button
            onClick={handleSnooze}
            disabled={busy}
            style={{ ...buttonStyle, background: 'none', border: '1px solid #555', color: '#999' }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};
